import { keywordGroupsQueryClient, keywordGroupsTransforms } from "app/services/keyword-groups";
import { ALL } from "app/common/services/categoryService";
import type { ICategory } from "app/common/services/categoryService.types";
import type { IDomainsList } from "pro-features/components/DomainsFilter/Types";
import { createUniqueListOfAllDomains, getFilteredDomain } from "pro-features/components/DomainsFilter/Utils";
import type { IDataFetcher, IDataFetcherFactory } from "app/components/widget/widget-fetchers/IDataFetcher";
import type { IWidget } from "app/components/widget/widget-types/Widget";
import { ACTIVATION_USERDATA_API } from "app/pages/activation/services/constants";
import type { ProjectsResponseSchema } from "app/pages/activation/types/api";
import { webSourceToDeviceMap } from "app/common/keyword-research/clicks-over-time-graph/graphTypes";
import { getShareValue, getVolumeValue } from "app/pages/website-analysis/traffic-sources/search/common/utils";
import { DefaultFetchService, requestParams } from "app/services/fetchService";

import { getCategories, getDomains } from "../widget-utilities/KeywordAnalysisUtils";

const defaultSort = { field: "Share", sortDirection: "asc" };
const fetchService = DefaultFetchService.getInstance();

const getActivationSets = async () =>
    window.similarweb.settings.components.Activation.isDisabled
        ? []
        : (await fetchService.get<ProjectsResponseSchema | null>(ACTIVATION_USERDATA_API))?.activationSets;

class KeywordAnalysisDataFetcher<T> implements IDataFetcher<T> {
    protected widget: IWidget<T>;
    constructor(widget) {
        this.widget = widget;
    }
    async fetch(apiParams = this.widget.apiParams as any) {
        const activationSets = await getActivationSets();

        const { ...params } = apiParams;
        const allDomains = createUniqueListOfAllDomains(params.domains?.split(","), activationSets);

        const _params = {
            page: 1,
            pageSize: 100,
            country: params.country,
            to: params.to,
            from: params.from,
            isWindow: params.isWindow,
            isDaily: params.isDaily,
            webSource: params.webSource,
            orderBy: params.orderBy ?? defaultSort.field,
            sort: params.sort ?? defaultSort.field,
            keys: params.keys,
            includeSubDomains: params.includeSubDomains,
            Device: webSourceToDeviceMap[params.webSource],
            funcFlag: params.funcFlag,
            sourceType: params.sourceType,
            selectedPageTab: params.selectedPageTab,
            category: params.category === ALL ? undefined : params.category,
            DomainsFilter: allDomains,
        };

        const paramsAsString = requestParams(_params);

        const categories = await getCategories(this.widget._metricConfig.categoriesEndpoint, paramsAsString);
        const domains = await getDomains(this.widget._metricConfig.domainsEndpoint, paramsAsString);
        const domainsList = getFilteredDomain(domains, activationSets);

        return fetchService
            .get(`${this.widget._metricConfig.endpoint}?${paramsAsString}`)
            .then(this.transformData(apiParams, categories, domainsList)) as any;
    }

    transformData = (apiParams, categories: ICategory[], domainsList: IDomainsList[]) => async (data) => {
        const isKeywordGroup = keywordGroupsTransforms.isKeywordGroup(apiParams.keyword);
        const transformedData = data?.Data.map(async (record) => {
            const shareOfDesktop = record.DesktopClicks / record.OriginalClicks;
            const shareOfOrganicClicks = record.OrganicClicks / record.OriginalClicks;

            const keywordNameForSERP = isKeywordGroup
                ? (await keywordGroupsQueryClient.getGroupById(apiParams.keyword))?.Name
                : apiParams.keyword;
            return {
                ...record,
                selectable: true,
                url: record.Domain,
                WebSourceShare: shareOfDesktop,
                SearchTerm: keywordNameForSERP,
                SiteSerpFeatures: { [record.Domain]: record.SerpFeatures },
                SerpScrapeDate: record.LatestKeywordSerpDate,
                OrganicClicksShare: shareOfOrganicClicks,
                Share: getShareValue(record.Share),
                Clicks: getVolumeValue(record.Clicks),
                totalMonthlyVolume: getVolumeValue(record.Volume),
                totalWindowVolume: getVolumeValue(record.Volume),
                AvgPos: record.Position,
                AvgCompetition: record.Competition,
                AvgDifficulty: record.Difficulty,
                TopIntent: {
                    PrimaryIntent: record.PrimaryIntent,
                    SecondaryIntent: record.SecondaryIntent,
                },
            };
        });
        return {
            ...data,
            Data: await Promise.all(transformedData),
            Categories: categories,
            DomainsList: domainsList,
        };
    };

    destroy() {
        return undefined;
    }
}
const KeywordAnalysisDataFetcherFactory: IDataFetcherFactory<any> = {
    create: function (widget) {
        return new KeywordAnalysisDataFetcher<any>(widget);
    },
};

export default KeywordAnalysisDataFetcherFactory;
